import React, {useEffect, useState} from 'react';
import QRCode from "react-qr-code";

export const CharacterReady = ({isControl}) => {
    const [data, setData] = useState(null);


    useEffect(() => {
        fetch('/users/current', {credentials: 'include'})
            .then(r => r.ok ? r.json() : null)
            .then(setData)
            .catch(() => setData(null));
    }, []);

    if (isControl) return <ControlJoinView/>;
    if (!data) return null;
    return <PlayerReadyView data={data}/>;
};

// Used in the CharacterReady control scene — fixed 1600x900 stage that
// matches the rest of the show's control views.
const ControlJoinView = () => (
    <>
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: '#000',
            zIndex: -1,
            pointerEvents: 'none',
        }}/>
        <div style={{
            alignSelf: 'flex-start',
            width: '1600px',
            height: '900px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-around',
            padding: '0 80px',
            boxSizing: 'border-box',
        }}>
            <img
                src="/party-machine-logo.png"
                alt="Party Machine"
                style={{
                    maxWidth: '55%',
                    maxHeight: '70%',
                    objectFit: 'contain',
                    filter: 'drop-shadow(4px 4px 0 rgba(0,0,0,0.85))',
                }}
            />
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 24}}>
                <div style={{
                    padding: 24,
                    background: 'white',
                    borderRadius: 12,
                    boxShadow: '6px 6px 0 rgba(0,0,0,0.6)',
                }}>
                    <QRCode value="https://play.imgoingto.party" size={420} level="H"/>
                </div>
                <div style={{
                    fontSize: 56,
                    fontWeight: 900,
                    fontStyle: 'italic',
                    letterSpacing: 4,
                    color: 'white',
                    textShadow: '3px 3px 0 rgba(0,0,0,0.85)',
                }}>play.imgoingto.party</div>
            </div>
        </div>
    </>
);

// Standalone /join route — responsive: side-by-side on wide displays,
// stacked on narrow/portrait. Use this for projector/laptop/phone screens
// outside the show's normal scene flow.
export const JoinPage = () => {
    const [isWide, setIsWide] = useState(true);
    useEffect(() => {
        const mq = window.matchMedia('(min-aspect-ratio: 1/1)');
        const update = () => setIsWide(mq.matches);
        update();
        mq.addEventListener ? mq.addEventListener('change', update) : mq.addListener(update);
        return () => {
            mq.removeEventListener ? mq.removeEventListener('change', update) : mq.removeListener(update);
        };
    }, []);

    const qrSize = isWide ? 'min(48vh, 480px)' : 'min(70vw, 480px)';

    return <>
        <div style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: '#000',
            zIndex: -1,
            pointerEvents: 'none',
        }}/>
        <div style={{
            width: '100vw',
            minHeight: '100dvh',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: isWide ? 'row' : 'column',
            alignItems: 'center',
            justifyContent: 'space-around',
            padding: isWide ? '0 5vw' : '5vh 0',
            gap: isWide ? '4vw' : '4vh',
        }}>
            <img
                src="/party-machine-logo.png"
                alt="Party Machine"
                style={{
                    maxWidth: isWide ? '50%' : '85%',
                    maxHeight: isWide ? '70%' : '40vh',
                    objectFit: 'contain',
                    filter: 'drop-shadow(4px 4px 0 rgba(0,0,0,0.85))',
                }}
            />
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 'clamp(12px, 2vh, 24px)'}}>
                <div style={{
                    padding: 'clamp(12px, 2.5vh, 24px)',
                    background: 'white',
                    borderRadius: 12,
                    boxShadow: '6px 6px 0 rgba(0,0,0,0.6)',
                    width: qrSize,
                    height: qrSize,
                    boxSizing: 'border-box',
                }}>
                    <QRCode value="https://play.imgoingto.party" size={256} level="H" style={{height: '100%', width: '100%'}}/>
                </div>
                <div style={{
                    fontSize: 'clamp(28px, 5vw, 56px)',
                    fontWeight: 900,
                    fontStyle: 'italic',
                    letterSpacing: 4,
                    color: 'white',
                    textShadow: '3px 3px 0 rgba(0,0,0,0.85)',
                }}>play.imgoingto.party</div>
            </div>
        </div>
    </>;
};

const PlayerReadyView = ({data}) => {
    const {name, token, character} = data;
    const sidePad = {paddingLeft: 24, paddingRight: 24};
    return (
        <div style={{
            width: '100%',
            margin: 0,
            boxSizing: 'border-box',
            alignSelf: 'flex-start',
            minHeight: '100vh',
            overflowX: 'hidden',
            position: 'relative',
            paddingTop: 'calc(36px + env(safe-area-inset-top))',
            paddingBottom: 24,
        }}>
            <div style={{
                position: 'fixed',
                inset: 0,
                background: 'radial-gradient(ellipse at top, rgba(54,255,54,0.15) 0%, #000 60%)',
                zIndex: -1,
                pointerEvents: 'none',
            }}/>
            <div style={{
                ...sidePad,
                fontSize: 40,
                fontWeight: 'bold',
                textAlign: 'center',
            }}>
                Ready to Play ✅
            </div>
            <hr style={{...sidePad, border: 0, borderTop: '1px solid white', margin: '4px 0 12px'}}/>
            <div style={{...sidePad, fontSize: 36, textAlign: 'center', marginBottom: 8}}>
                {name}
            </div>
            {character && character.nickname && (
                <div style={{...sidePad, fontSize: 24, textAlign: 'center', marginBottom: 20, fontStyle: 'italic', opacity: 0.85}}>
                    {character.nickname}
                </div>
            )}
            {character && character.image_url && (
                <img
                    src={character.image_url}
                    alt="character"
                    style={{width: '100%', display: 'block', marginBottom: 36}}
                />
            )}
            <div style={{
                ...sidePad,
                marginLeft: 24,
                marginRight: 24,
                paddingLeft: 0,
                paddingRight: 0,
            }}>
                <div style={{
                    padding: 20,
                    border: '2px solid #36ff36',
                    borderRadius: 8,
                    textAlign: 'center',
                    marginBottom: 24,
                }}>
                    <div style={{fontSize: 14, opacity: 0.7, marginBottom: 6}}>YOUR CODE WORD</div>
                    <div style={{fontSize: 42, fontWeight: 'bold', color: '#36ff36', letterSpacing: 2}}>{token}</div>
                    <div style={{fontSize: 14, opacity: 0.8, marginTop: 10}}>Screenshot this. You'll need it if you get logged out.</div>
                </div>
            </div>
        </div>
    );
};
